import { synchronize } from '@nozbe/watermelondb/sync'
import { Q } from '@nozbe/watermelondb'
import { countUnsyncedRecords, database, isDatabaseReady, txn } from '../db'
import {
    renumberRejectedInvoices,
    describeRenumbering,
    recordRenumbering,
    type SyncRejection,
} from './invoiceRenumber'
import { useAppStore } from '../store/useAppStore'
import { API_URL } from '../config'
import { Batch, Product, Supplier } from '../db/models/Inventory'
import { Customer } from '../db/models/Customers'
import { Promotion } from '../db/models/System'
import { useConflictStore } from '../store/useConflictStore'
import { API_BASE_URL } from '../config'
import ErrorTracking from '../services/ErrorTrackingService'
import {
    DEFAULT_FETCH_TIMEOUT_MS,
    SYNC_PULL_TIMEOUT_MS,
    SYNC_PUSH_TIMEOUT_MS,
    AUTH_FETCH_TIMEOUT_MS,
} from '../config/timeouts'
import { mergeChanges, type SyncTableChanges } from './mergeChanges'
import { applyServerStock, applyServerBatchQty, refreshOverlay } from './liveStock'
import { highestSequenceForSlot } from '../billing/invoiceNumberRecovery'
import { Invoice } from '../db/models/Billing'

/** Shape of `/sync/pull` response from the backend. */
interface PullResponseBody {
    changes: Record<string, SyncTableChanges>;
    timestamp: number;
    _incomplete?: string[];
    _cursors?: Record<string, { updatedAt: number; id: string }>;
    _pullTimestamp?: number;
    _hasMore?: boolean;
    deviceSlot?: number | null;
    /**
     * Why this device has no billing slot, when it has none.
     *
     * The pull still succeeds without a slot — reading and billing are
     * different permissions. This carries the reason so the till can say
     * "device not registered" rather than leaving the operator to discover it
     * at the moment they try to take money.
     */
    slotError?: string | null;
}

/** Shape of `/sync/push` response stats from the backend. */
interface PushResponseStats {
    conflicts?: { table: string; id: string }[];
    /**
     * Rows the server refused by name — currently invoices whose number was
     * already taken in the store. Refused, not dropped: the client renumbers
     * and re-sends. See invoiceRenumber.ts.
     */
    rejected?: SyncRejection[];
    [key: string]: unknown;
}

/**
 * Rejections captured during the push, applied AFTER synchronize() returns.
 *
 * Not applied inline: WatermelonDB marks everything it pushed as synced once
 * the push resolves, including the row the server threw away, so a renumber
 * done here would be erased by that bookkeeping and the invoice would sit
 * locally marked clean and never re-sent.
 */
let capturedRejections: SyncRejection[] = [];

let syncPromise: Promise<boolean> | null = null;

const MAX_PULL_PAGES = 100;

/**
 * Write a summary record to the local sync_logs table.
 * Falls back to console + ErrorTracking if the DB write fails
 * (e.g. disk full, database locked, schema mismatch).
 */
async function logSyncResult(params: {
    deviceId: string;
    merchantId: string | null;
    direction: 'FULL' | 'PULL' | 'PUSH';
    status: 'SUCCESS' | 'ERROR';
    durationMs: number;
    recordCount: number;
    errorMessage?: string;
}): Promise<void> {
    try {
        await txn(async (db) => {
            await db.get('sync_logs').create((record: any) => {
                record.merchantId = params.merchantId;
                record.deviceId = params.deviceId;
                record.direction = params.direction;
                record.tableName = '_summary';
                record.recordCount = params.recordCount;
                record.status = params.status;
                record.errorMessage = params.errorMessage ?? null;
                record.durationMs = params.durationMs;
            });
        });
    } catch (dbErr) {
        // DB write failed — fall back to console so the info isn't lost
        console.warn('[Sync] Failed to write sync log to DB, falling back to console:', dbErr);
        console.log('[Sync Log Fallback]', JSON.stringify(params));
        ErrorTracking.captureMessage(
            `Sync log DB write failed: ${(dbErr as Error).message}`,
            'warning',
        );
    }
}

const SYNC_LOG_RETENTION_DAYS = 90;
let lastCleanupRun = 0; // epoch ms — run at most once per day

/**
 * Prune sync_logs older than SYNC_LOG_RETENTION_DAYS.
 * Runs at most once per 24h to avoid unnecessary work.
 */
async function cleanupOldSyncLogs(): Promise<void> {
    const now = Date.now();
    if (now - lastCleanupRun < 86_400_000) return; // already ran today
    lastCleanupRun = now;

    try {
        const cutoff = now - SYNC_LOG_RETENTION_DAYS * 86_400_000;
        const oldLogs = await database.get('sync_logs')
            .query(Q.where('created_at', Q.lt(cutoff)))
            .fetch();

        if (oldLogs.length === 0) return;

        await txn(async (db) => {
            await db.batch(
                ...oldLogs.map(log => log.prepareDestroyPermanently()),
            );
        });
        console.log(`[Sync] Cleaned up ${oldLogs.length} sync log(s) older than ${SYNC_LOG_RETENTION_DAYS} days.`);
    } catch (e) {
        console.warn('[Sync] Sync log cleanup failed (non-fatal):', e);
    }
}
const MAX_SYNC_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 2000;

// ── Error Classification ─────────────────────────────────────────────────────
// Determines whether a sync error is transient (worth retrying) or fatal.

class SyncError extends Error {
    constructor(
        message: string,
        public readonly retryable: boolean,
        /**
         * True when retrying inside THIS cycle cannot help, only a later one.
         * Rate limiting is the case: the window is a minute and the in-cycle
         * budget is about fourteen seconds, so burning it achieves nothing but
         * three more rejected requests.
         */
        public readonly deferToNextCycle = false,
    ) {
        super(message);
        this.name = 'SyncError';
    }
}

function classifyError(err: unknown): SyncError {
    const msg = (err as Error)?.message || '';
    const status = (err as { status?: number })?.status || 0;

    // Fatal: auth failures — retrying won't help
    if (status === 401 || status === 403 || msg.includes('Session expired') || msg.includes('please login')) {
        return new SyncError(msg, false);
    }
    // Fatal: validation / bad request
    if (status === 400 || status === 422) {
        return new SyncError(msg, false);
    }
    // Rate limited. Retryable, but never within this cycle.
    //
    // This used to fall through to the unknown-error default and retry three
    // times over ~14s against a 60s window — three more 429s and then a silent
    // give-up. A push lost this way takes the till's data with it: everything
    // stays in IndexedDB while the screen shows a healthy store. A brand-new
    // merchant's first store was lost exactly here.
    if (status === 429 || msg.includes('ThrottlerException') || msg.includes('Too Many Requests')) {
        return new SyncError(
            'Server is rate limiting this device — sync will resume shortly.',
            true,
            true,
        );
    }
    // Transient: timeouts, server errors, network failures
    if (
        status === 503 || status === 502 || status === 504 || status >= 500 ||
        msg.includes('Timeout') || msg.includes('timeout') ||
        msg.includes('Network request failed') || msg.includes('AbortError') ||
        msg.includes('ECONNREFUSED') || msg.includes('ENOTFOUND') ||
        msg.includes('fetch failed')
    ) {
        return new SyncError(msg, true);
    }
    // Default: treat unknown errors as retryable once
    return new SyncError(msg, true);
}

/** Exponential backoff with jitter: base * 2^attempt + random(0..base) */
function retryDelay(attempt: number): number {
    return BASE_RETRY_DELAY_MS * Math.pow(2, attempt) + Math.random() * BASE_RETRY_DELAY_MS;
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null (triggers logout).
 */
async function tryRefreshToken(): Promise<string | null> {
    const { refreshToken, setAuthToken, setRefreshToken, logout } = useAppStore.getState();
    if (!refreshToken) {
        console.warn('[Auth] No refresh token available — logging out');
        logout();
        return null;
    }

    try {
        console.log('[Auth] Access token expired, attempting refresh...');
        const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) {
            console.warn(`[Auth] Refresh failed (${res.status}) — logging out`);
            logout();
            return null;
        }

        const data = await res.json();
        if (data.success && data.access_token) {
            setAuthToken(data.access_token);
            if (data.refresh_token) setRefreshToken(data.refresh_token);
            console.log('[Auth] Token refreshed successfully');
            return data.access_token;
        }

        logout();
        return null;
    } catch (e: unknown) {
        console.error('[Auth] Token refresh error:', (e as Error).message);
        logout();
        return null;
    }
}

/**
 * Raise this device's invoice sequence past every number it has already issued.
 *
 * Runs after each successful sync, when the pull has just restored the store's
 * invoices. Guards the case where browser storage was cleared or evicted and the
 * counter restarted at 1 — see billing/invoiceNumberRecovery.ts.
 *
 * Best-effort by design: a failure here must never fail a sync that otherwise
 * succeeded. The worst case is that it runs again on the next cycle.
 */
async function recoverInvoiceSequence(storeId: string): Promise<void> {
    try {
        const { deviceSlot, raiseDeviceInvoiceCounter } = useAppStore.getState();
        // Without a slot this device cannot bill at all (useCreateInvoice
        // refuses), so there is no sequence to protect yet.
        if (deviceSlot == null) return;

        const invoices = await database
            .get<Invoice>('invoices')
            .query(Q.where('store_id', storeId))
            .fetch();

        // Deleted invoices are included deliberately: a voided or removed bill
        // has still spent its number, and reissuing it would be the same
        // duplicate this exists to prevent.
        const highest = highestSequenceForSlot(
            invoices.map((i) => i.invoiceNumber).filter(Boolean),
            deviceSlot,
        );
        if (highest > 0) await raiseDeviceInvoiceCounter(highest);
    } catch (err) {
        console.warn(
            '[Sync] Invoice sequence recovery failed (will retry next sync):',
            (err as Error).message,
        );
    }
}

/**
 * Returns `true` when a full sync cycle completed, `false` when skipped
 * (already in progress, or not authenticated). Only `true` should update
 * `lastSyncAt` in the caller.
 *
 * Uses a promise-based lock: concurrent callers await the same promise
 * rather than racing on a boolean flag.
 */
export async function syncDatabase(options: { fullSync?: boolean } = {}): Promise<boolean> {
    if (syncPromise) {
        console.log('[Sync] Synchronization already in progress — awaiting current cycle.');
        return syncPromise;
    }

    // Full-sync forces a re-pull of every record by clearing WatermelonDB's
    // lastPulledAt cursor. Used when the user explicitly requests "Sync now"
    // because the local cache may have drifted from the server (e.g. a fresh
    // install or stale cursor past the data's updatedAt).
    if (options.fullSync) {
        try {
            console.log('[Sync] Full sync requested — clearing lastPulledAt cursor.');
            await database.adapter.setLocal('__watermelon_last_pulled_at', '0');
        } catch (e) {
            console.warn('[Sync] Failed to clear lastPulledAt before full sync:', (e as Error).message);
        }
    }

    syncPromise = doSyncWithRetry();
    try {
        return await syncPromise;
    } finally {
        syncPromise = null;
        // Refresh the count that `installUnloadGuard()` reads. The guard is
        // synchronous (browsers ignore async work in `beforeunload`), so it can
        // only consult a cached number — and before this the cache was fed
        // exclusively by the support-facing /diagnostics screen. On a real till
        // nobody opens that screen, so the count stayed 0 and closing a tab
        // holding unsynced sales warned about nothing. A sync is exactly when
        // the number changes, so refresh it here.
        void refreshUnsyncedAfterSync();
    }
}

/** Best-effort: never let a counting failure surface as a sync failure. */
async function refreshUnsyncedAfterSync(): Promise<void> {
    if (!isDatabaseReady()) return;
    try {
        await countUnsyncedRecords();
    } catch (e) {
        console.warn('[Sync] Unsynced recount failed:', (e as Error).message);
    }
}

/**
 * Wraps doSync() with retry logic for transient errors.
 * Fatal errors (401, 403) fail immediately without retry.
 */
async function doSyncWithRetry(): Promise<boolean> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_SYNC_RETRIES; attempt++) {
        try {
            if (attempt > 0) {
                const delay = retryDelay(attempt - 1);
                console.log(`[Sync] Retry ${attempt}/${MAX_SYNC_RETRIES} after ${Math.round(delay)}ms...`);
                ErrorTracking.addBreadcrumb(`Sync retry ${attempt}`, { delay: Math.round(delay) }, 'sync');
                await sleep(delay);
            }
            return await doSync();
        } catch (err: unknown) {
            const syncErr = err instanceof SyncError ? err : classifyError(err);
            lastError = syncErr;

            if (!syncErr.retryable) {
                console.error(`[Sync] Fatal error (not retryable): ${syncErr.message}`);
                throw syncErr;
            }

            // Retryable, but not on this pass — the wait outlives the budget.
            if (syncErr.deferToNextCycle) {
                console.warn(`[Sync] ${syncErr.message} Deferring to the next scheduled sync.`);
                throw syncErr;
            }

            if (attempt < MAX_SYNC_RETRIES) {
                console.warn(`[Sync] Transient error on attempt ${attempt + 1}: ${syncErr.message}`);
            }
        }
    }

    console.error(`[Sync] All ${MAX_SYNC_RETRIES} retries exhausted.`);
    throw lastError || new Error('Sync failed after retries');
}

async function doSync(): Promise<boolean> {
    const syncStartMs = Date.now();

    const { activeStoreId, authToken, deviceId, setDeviceSlot } = useAppStore.getState()

    if (!authToken || !activeStoreId) {
        console.warn('[Sync] Skipped: authToken or activeStoreId is missing.', { hasToken: !!authToken, activeStoreId });
        return false;
    }

    // Device ID is ensured at app startup via deviceIdentity service.
    // If somehow still missing, fail fast — the renderer must hydrate first.
    let currentDeviceId = deviceId;
    if (!currentDeviceId) {
        const msg = 'Device ID missing at sync time — hydrateAppStore() must run at startup.';
        console.error('[Sync]', msg);
        ErrorTracking.captureMessage(msg, 'error');
        throw new Error(msg);
    }

    // Helper for timeout
    const fetchWithTimeout = async (resource: string, options: RequestInit & { timeout?: number } = {}) => {
        const { timeout = DEFAULT_FETCH_TIMEOUT_MS } = options;

        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(resource, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(id);
            return response;
        } catch (error: unknown) {
            clearTimeout(id);
            if ((error as Error).name === 'AbortError') {
                throw new Error(`[Timeout] Request to ${resource} took longer than ${timeout}ms`);
            }
            throw error;
        }
    };

    /**
     * Make a single authenticated fetch with auto-refresh on 401.
     * Returns the parsed JSON response.
     */
    const authenticatedFetch = async (url: string, options: RequestInit & { timeout?: number } = {}): Promise<{ json: Record<string, unknown>; token: string }> => {
        let currentToken = useAppStore.getState().authToken || authToken;

        let response = await fetchWithTimeout(url, {
            ...options,
            timeout: options.timeout || AUTH_FETCH_TIMEOUT_MS,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${currentToken}`,
                'Content-Type': 'application/json',
            },
        });

        // Auto-refresh on 401 and retry once
        if (response.status === 401) {
            const newToken = await tryRefreshToken();
            if (!newToken) throw new Error('[Auth] Session expired — please login again');
            currentToken = newToken;
            response = await fetchWithTimeout(url, {
                ...options,
                timeout: options.timeout || AUTH_FETCH_TIMEOUT_MS,
                headers: {
                    ...options.headers,
                    Authorization: `Bearer ${currentToken}`,
                    'Content-Type': 'application/json',
                },
            });
        }

        if (!response.ok) {
            const text = await response.text();
            const err = new Error(`HTTP ${response.status}: ${text}`) as Error & { status: number };
            err.status = response.status;
            throw err;
        }

        return { json: await response.json(), token: currentToken };
    };

    // Phase 2: server-authoritative product/batch stock captured during the pull, applied
    // to the live-stock display snapshot after the sync succeeds.
    const capturedServerStock: Record<string, number> = {};
    const capturedServerBatchQty: Record<string, number> = {};

    const finishSyncTransaction = ErrorTracking.startTransaction('sync.full', 'sync');
    ErrorTracking.addBreadcrumb('Sync started', { storeId: activeStoreId, deviceId: currentDeviceId }, 'sync');
    ErrorTracking.setSyncContext({ isSyncing: true, lastSyncAt: null, syncError: null });

    try {
        console.log('[Sync] Starting WatermelonDB synchronization...');
        await synchronize({
            database,

            // Tells WatermelonDB to merge 'created' records into the 'updated'
            // array during push. This is CRITICAL because the NestJS backend CRDT upsert logic
            // natively expects to process all conflicts via version-diffing.
            sendCreatedAsUpdated: true,

            pullChanges: async ({ lastPulledAt, schemaVersion }) => {
                const allChanges: Record<string, SyncTableChanges> = {};
                let finalTimestamp = 0;
                // #3 fix: if ANY page reports incomplete/errored tables, do NOT advance
                // lastPulledAt — otherwise a later page auto-completes the errored table
                // and the cursor skips its unread rows permanently.
                let anyIncomplete = false;
                let cursors: Record<string, { updatedAt: number; id: string }> | undefined;
                let pullTimestamp: number | undefined;
                let pageCount = 0;

                while (pageCount < MAX_PULL_PAGES) {
                    pageCount++;

                    // Build URL with pagination params
                    let urlQuery = `lastPulledAt=${lastPulledAt || 0}&storeId=${activeStoreId}&deviceId=${currentDeviceId}`;
                    if (schemaVersion) urlQuery += `&schemaVersion=${schemaVersion}`;
                    if (cursors) urlQuery += `&_cursors=${encodeURIComponent(JSON.stringify(cursors))}`;
                    if (pullTimestamp) urlQuery += `&_pullTimestamp=${pullTimestamp}`;

                    const finalUrl = `${API_URL}/sync/pull?${urlQuery}`;
                    console.log(`[Sync Pull] Page ${pageCount}: fetching...`);

                    try {
                        const { json: responseJson } = await authenticatedFetch(finalUrl, { timeout: SYNC_PULL_TIMEOUT_MS });
                        const pullBody = responseJson as unknown as PullResponseBody;

                        const {
                            changes,
                            timestamp,
                            _incomplete,
                            _cursors: nextCursors,
                            _pullTimestamp: nextPullTimestamp,
                            _hasMore,
                            deviceSlot,
                            slotError,
                        } = pullBody;

                        // Save device slot from first page only
                        if (pageCount === 1) {
                            if (deviceSlot !== undefined && deviceSlot !== null) {
                                setDeviceSlot(deviceSlot);
                                useAppStore.getState().setDeviceSlotError(null);
                            } else if (slotError) {
                                // Pull succeeded, billing did not become available.
                                useAppStore.getState().setDeviceSlotError(slotError);
                            }
                        }

                        // Merge this page's changes into the accumulated result
                        mergeChanges(allChanges, changes);
                        finalTimestamp = timestamp;

                        // Count records on this page for logging
                        let pageRecords = 0;
                        for (const t of Object.values(changes) as SyncTableChanges[]) {
                            pageRecords += (t.created?.length || 0) + (t.updated?.length || 0) + (t.deleted?.length || 0);
                        }
                        console.log(`[Sync Pull] Page ${pageCount}: ${pageRecords} records received.`);

                        if (_incomplete && _incomplete.length > 0) {
                            anyIncomplete = true;
                            console.warn(
                                `[Sync Pull] Partial pull — ${_incomplete.length} table(s) incomplete: [${_incomplete.join(', ')}]. ` +
                                `Timestamp NOT advanced — will re-pull on next sync.`
                            );
                            // Surface incomplete tables to the user via syncError
                            useAppStore.getState().setSyncError(
                                `Sync incomplete: ${_incomplete.length} table(s) timed out (${_incomplete.slice(0, 3).join(', ')}${_incomplete.length > 3 ? '...' : ''}). Will retry on next sync.`
                            );
                            ErrorTracking.captureMessage(
                                `Sync pull incomplete: ${_incomplete.join(', ')}`,
                                'warning',
                            );
                        }

                        // If no more pages, we're done
                        if (!_hasMore || !nextCursors || Object.keys(nextCursors).length === 0) {
                            console.log(`[Sync Pull] Complete after ${pageCount} page(s).`);
                            break;
                        }

                        // Prepare for next page
                        cursors = nextCursors;
                        pullTimestamp = nextPullTimestamp;
                        console.log(`[Sync Pull] ${Object.keys(nextCursors).length} table(s) have more data — fetching next page...`);

                    } catch (err: unknown) {
                        console.error(`[Sync Pull Failed] Page ${pageCount}: ${(err as Error).message}`);
                        ErrorTracking.captureException(err as Error, {
                            screen: 'Sync',
                            action: 'pullChanges',
                            page: pageCount,
                            storeId: activeStoreId,
                        });
                        const classified = classifyError(err);
                        throw classified;
                    }
                }

                if (pageCount >= MAX_PULL_PAGES) {
                    const msg = `Sync reached page limit (${MAX_PULL_PAGES}). Some data may not be synced yet — sync again to continue.`;
                    console.warn(`[Sync Pull] ${msg}`);
                    useAppStore.getState().setSyncError(msg);
                    ErrorTracking.captureMessage(`Sync pull hit MAX_PULL_PAGES (${MAX_PULL_PAGES})`, 'warning');
                }

                // Log total records across all pages
                let totalRecords = 0;
                for (const t of Object.values(allChanges)) {
                    totalRecords += t.created.length + t.updated.length + t.deleted.length;
                }
                console.log(`[Sync Pull] Total: ${totalRecords} records across ${Object.keys(allChanges).length} tables in ${pageCount} page(s).`);

                // Phase 2: capture the server-authoritative product currentStock from this
                // pull so the live-stock display can show it + this device's unsynced overlay.
                const pulledProducts = allChanges['products'];
                if (pulledProducts) {
                    for (const r of [...(pulledProducts.created ?? []), ...(pulledProducts.updated ?? [])] as Array<Record<string, number>>) {
                        if (r.id != null && r.current_stock != null) {
                            capturedServerStock[r.id as unknown as string] = r.current_stock;
                        }
                    }
                }
                const pulledBatches = allChanges['batches'];
                if (pulledBatches) {
                    for (const r of [...(pulledBatches.created ?? []), ...(pulledBatches.updated ?? [])] as Array<Record<string, number>>) {
                        if (r.id != null && r.quantity != null) {
                            capturedServerBatchQty[r.id as unknown as string] = r.quantity;
                        }
                    }
                }

                // When incomplete, pin the cursor at the prior position (never advance past
                // unread rows). `|| 1` keeps it a valid non-zero timestamp on first sync.
                return { changes: allChanges, timestamp: anyIncomplete ? (lastPulledAt || 1) : finalTimestamp };
            },
            pushChanges: async ({ changes, lastPulledAt }) => {
                const typedChanges = changes as unknown as Record<string, SyncTableChanges>;
                const activeTables = Object.entries(typedChanges)
                    .filter(([, data]) => data.created.length > 0 || data.updated.length > 0 || data.deleted.length > 0)
                    .map(([table]) => table);

                if (activeTables.length > 0) {
                    // Log a pre-push snapshot of change counts per table for diagnostics.
                    // If the push fails, this record shows exactly what was being sent.
                    const changeSnapshot: Record<string, string> = {};
                    for (const table of activeTables) {
                        const t = (changes as Record<string, SyncTableChanges>)[table];
                        changeSnapshot[table] = `C${t.created.length}/U${t.updated.length}/D${t.deleted.length}`;
                    }
                    console.log(`[Sync Push] Pre-push snapshot:`, JSON.stringify(changeSnapshot));
                } else {
                    console.log(`[Sync Push] No local changes to send.`);
                }

                const pushId = 'push_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 10);

                const pushBody = JSON.stringify({
                    changes,
                    lastPulledAt,
                    storeId: activeStoreId,
                    deviceId: currentDeviceId,
                    pushId,
                });

                try {
                    const { json: stats } = await authenticatedFetch(`${API_URL}/sync/push`, {
                        timeout: SYNC_PUSH_TIMEOUT_MS,
                        method: 'POST',
                        body: pushBody,
                    });
                    const pushStats = stats as unknown as PushResponseStats;

                    console.log(`[Sync Push Success] Stats:`, pushStats);

                    // Refused rows — held for after the cycle, see capturedRejections.
                    if (pushStats.rejected && pushStats.rejected.length > 0) {
                        console.warn(`[Sync Push] ${pushStats.rejected.length} row(s) refused by server:`, pushStats.rejected);
                        capturedRejections = capturedRejections.concat(pushStats.rejected);
                    }

                    // Surface conflicts to the user via the conflict store
                    if (pushStats.conflicts && pushStats.conflicts.length > 0) {
                        console.log(`[Sync Push] ${pushStats.conflicts.length} conflict(s) resolved by server:`, pushStats.conflicts);
                        useConflictStore.getState().setConflicts(pushStats.conflicts);
                    }

                    // The server strips tables a role isn't allowed to push. If a
                    // STRIPPED table carried real rows, that's data loss — never let
                    // it pass as a clean success. (Should never fire once role write
                    // permissions are correct; this is the safety net for future drift.)
                    const stripped = (pushStats as { stripped?: string[] }).stripped;
                    if (stripped && stripped.length > 0) {
                        const msg = `Server rejected changes to: ${stripped.join(', ')}. Your role cannot sync these — contact the store owner.`;
                        console.error(`[Sync Push] DATA DROPPED — ${msg}`);
                        useAppStore.getState().setSyncError(msg);
                        ErrorTracking.captureMessage(`Sync push stripped tables with data: ${stripped.join(', ')}`, 'error');
                        // #4 fix: THROW so WatermelonDB does not mark these local changes
                        // as synced (was previously only logged → silent data loss).
                        throw new Error(msg);
                    }
                } catch (err: unknown) {
                    console.error(`[Sync Push Failed] ${(err as Error).message}`);
                    ErrorTracking.captureException(err, {
                        screen: 'Sync',
                        action: 'pushChanges',
                        storeId: activeStoreId,
                        tables: activeTables.join(','),
                    });
                    const classified = classifyError(err);
                    throw classified;
                }
            },
            migrationsEnabledAtVersion: 1,
        })
        const durationMs = Date.now() - syncStartMs;
        console.log(`[Sync] Synchronization complete in ${durationMs}ms!`);

        // Phase 2: refresh the live-stock display — apply the server snapshot captured on
        // this pull, then recompute this device's unsynced overlay (some may now be synced).
        applyServerStock(capturedServerStock);
        applyServerBatchQty(capturedServerBatchQty);
        await refreshOverlay();

        // The pull has just brought this store's invoices down. If browser
        // storage was cleared or evicted, the local sequence restarted at 1 and
        // this device would re-issue numbers it has already used — silently, as
        // the server has no uniqueness constraint. Recover the counter from what
        // is now on disk before the next sale can take a number.
        await recoverInvoiceSequence(activeStoreId);

        // Now that WatermelonDB has finished marking this cycle's rows, give any
        // refused invoice a fresh number. The update marks it dirty again, so the
        // NEXT push carries it — which is the whole point: the sale still reaches
        // the server, under a number nobody else has taken.
        if (capturedRejections.length > 0) {
            const rejections = capturedRejections;
            capturedRejections = [];
            const outcomes = await renumberRejectedInvoices(
                rejections,
                activeStoreId,
                useAppStore.getState().deviceSlot ?? null,
            );
            recordRenumbering(outcomes, activeStoreId);
            const message = describeRenumbering(outcomes);
            if (message) {
                // Loudly, but NOT as a sync error. This cycle SUCCEEDED — the
                // renumbering is the system working. Routing it through
                // setSyncError painted "SYNC FAILED" across a healthy till and
                // made the operator distrust a sync that had just done its job.
                // recordRenumbering above is what reaches the screen.
                console.warn(`[Sync] ${message}`);
                ErrorTracking.captureMessage(message, 'warning');
            }
        }

        finishSyncTransaction('ok');
        ErrorTracking.addBreadcrumb('Sync completed successfully', { storeId: activeStoreId }, 'sync');
        ErrorTracking.setSyncContext({ isSyncing: false, lastSyncAt: new Date().toISOString(), syncError: null });

        // Log sync result (fire-and-forget with DB fallback)
        logSyncResult({
            deviceId: currentDeviceId,
            merchantId: useAppStore.getState().currentUser?.merchantId ?? null,
            direction: 'FULL',
            status: 'SUCCESS',
            durationMs,
            recordCount: 0, // summary record — detail is in console logs
        }).catch(() => {});

        // Prune old sync logs (at most once per day, non-blocking)
        cleanupOldSyncLogs().catch(() => {});
    } catch (err) {
        const durationMs = Date.now() - syncStartMs;
        const errMsg = (err as Error).message;
        console.error('[Sync] Synchronization failed:', errMsg);
        finishSyncTransaction('error');
        ErrorTracking.setSyncContext({ isSyncing: false, lastSyncAt: null, syncError: errMsg });
        ErrorTracking.captureException(err, { screen: 'Sync', action: 'syncDatabase', storeId: activeStoreId }, 'error');

        // Log sync failure (fire-and-forget with DB fallback)
        logSyncResult({
            deviceId: currentDeviceId,
            merchantId: useAppStore.getState().currentUser?.merchantId ?? null,
            direction: 'FULL',
            status: 'ERROR',
            durationMs,
            recordCount: 0,
            errorMessage: errMsg,
        }).catch(() => {});

        throw err;
    }
    return true;
}
